import { useState, useEffect } from "react";
import { ApiError } from "@/lib/api-client";
import { slugify } from "@/lib/slugify";
import { useJobTypeForms, type CreatedJobTypeForm } from "./useJobTypeForms";
import styles from "./AddFormDialog.module.css";

interface AddFormDialogProps {
  jobTypeId: string;
  open: boolean;
  onClose: () => void;
  /** Called with the newly-created form on success (blank or clone). */
  onCreated?: (form: CreatedJobTypeForm) => void;
}

/** Map a known 409 error code to a friendly inline message. */
function messageForError(err: unknown): string {
  if (err instanceof ApiError) {
    const code = (err.body as { error?: string } | null)?.error;
    if (code === "duplicate_name") return "A form with that name already exists.";
    if (code === "duplicate_classification_key")
      return "A form with that classification key already exists.";
  }
  return "Could not add the form. Please try again.";
}

/**
 * AddFormDialog — create a new form either blank (`create`) or by deep-cloning
 * an existing form (`clone`). Mirrors export/AddDestinationDialog conventions.
 */
export function AddFormDialog({ jobTypeId, open, onClose, onCreated }: AddFormDialogProps) {
  const { forms, create, clone } = useJobTypeForms(jobTypeId);
  const [mode, setMode] = useState<"blank" | "clone">("blank");
  const [sourceId, setSourceId] = useState<string>("");
  const [name, setName] = useState("");
  const [classificationKey, setClassificationKey] = useState("");
  // F-040 — while the key field is untouched, it mirrors the slugified name.
  // Once the user edits the key directly, stop mirroring so we never clobber it.
  const [keyTouched, setKeyTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // The dialog stays mounted across close→reopen (parent renders it
  // unconditionally), so reset all fields whenever it opens — otherwise
  // name/key/mode/source/error from a prior session would persist.
  useEffect(() => {
    if (open) {
      setName("");
      setClassificationKey("");
      setKeyTouched(false);
      setMode("blank");
      setSourceId("");
      setError(null);
    }
  }, [open]);

  if (!open) return null;

  // Default the clone source to the first available form.
  const effectiveSource = sourceId || forms[0]?.id || "";

  // F-040 — the classification key is the routing label the runtime matches
  // against (`form.classificationKey === doc.classification`), so it must be a
  // real value — but it defaults to the slugified name instead of forcing the
  // user to invent one (the default form's key is auto-assigned `__default__`).
  const effectiveKey = classificationKey.trim() || slugify(name);

  const canSubmit =
    !!name.trim() &&
    !!effectiveKey &&
    (mode === "blank" || !!effectiveSource) &&
    !submitting;

  function handleNameChange(value: string) {
    setName(value);
    // Mirror the slug into the key only while the user hasn't edited it.
    if (!keyTouched) setClassificationKey(slugify(value));
  }

  async function handleAdd() {
    if (!name.trim() || !effectiveKey) return;
    if (mode === "clone" && !effectiveSource) return;
    setSubmitting(true);
    setError(null);
    const args = { name: name.trim(), classificationKey: effectiveKey };
    try {
      const form =
        mode === "clone" ? await clone(effectiveSource, args) : await create(args);
      onCreated?.(form);
      onClose();
    } catch (err) {
      setError(messageForError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className={styles.dialogOverlay}
      role="dialog"
      aria-modal="true"
      aria-label="Add form"
    >
      <div className={styles.dialog}>
        <h2 className={styles.dialogTitle}>Add Form</h2>

        <div className={styles.modeGroup} role="radiogroup" aria-label="Source">
          <label className={styles.modeOption}>
            <input
              type="radio"
              name="add-form-mode"
              value="blank"
              checked={mode === "blank"}
              onChange={() => setMode("blank")}
            />
            Start blank
          </label>
          <label className={styles.modeOption}>
            <input
              type="radio"
              name="add-form-mode"
              value="clone"
              checked={mode === "clone"}
              onChange={() => setMode("clone")}
            />
            <span className={styles.cloneRow}>
              Clone from
              <select
                aria-label="Clone source form"
                value={effectiveSource}
                disabled={mode !== "clone"}
                onChange={(e) => setSourceId(e.target.value)}
              >
                {forms.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                  </option>
                ))}
              </select>
            </span>
          </label>
        </div>

        <div className={styles.dialogField}>
          <label htmlFor="add-form-name">Name</label>
          <input
            id="add-form-name"
            type="text"
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
            placeholder="Form name"
            autoFocus
          />
        </div>

        <div className={styles.dialogField}>
          <label htmlFor="add-form-key">Classification key</label>
          <input
            id="add-form-key"
            type="text"
            value={classificationKey}
            onChange={(e) => {
              setClassificationKey(e.target.value);
              setKeyTouched(true);
            }}
            placeholder={slugify(name) || "classification-key"}
          />
          <p className={styles.dialogHint}>
            The label a classifier, recognition hint, or source must produce to
            route a document to this form. Defaults from the name — edit it to
            match what your source declares.
          </p>
        </div>

        {error && (
          <div className={styles.errorText} role="alert">
            {error}
          </div>
        )}

        <div className={styles.dialogActions}>
          <button type="button" className={styles.dialogCancelBtn} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={styles.dialogAddBtn}
            onClick={handleAdd}
            disabled={!canSubmit}
          >
            Add
          </button>
        </div>
      </div>
    </div>
  );
}
